import {
  Box,
  ButtonBase,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { OrganizationNiche } from "schemas/dashboard";

import {
  TemplateContentType,
  TemplateFolder,
} from "features/Influencer/ContactList/schema";

interface TypeOption {
  value: TemplateContentType;
  label: string;
  icon: string;
  description: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  {
    value: TemplateContentType.OUTREACH,
    label: "Outreach",
    icon: "fa-regular fa-paper-plane",
    description: "Your first message to a brand to introduce yourself",
  },
  {
    value: TemplateContentType.FOLLOWUP,
    label: "Followup",
    icon: "fa-regular fa-arrow-rotate-right",
    description: "A short email if they didn't reply",
  },
];

interface HeaderControlsProps {
  typeValue: TemplateContentType;
  onTypeChange: (value: TemplateContentType) => void;
  showType: boolean;
  folderId: number | null | undefined;
  folders: TemplateFolder[];
  onFolderChange: (id: number | null) => void;
  onNewFolder: () => void;
}

export default function HeaderControls({
  typeValue,
  onTypeChange,
  showType,
  folderId,
  folders,
  onFolderChange,
  onNewFolder,
}: HeaderControlsProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {showType && <TypePill value={typeValue} onChange={onTypeChange} />}
      <FolderPill
        folderId={folderId ?? null}
        folders={folders}
        onChange={onFolderChange}
        onNewFolder={onNewFolder}
      />
    </Box>
  );
}

export function TypePill({
  value,
  onChange,
  compact = false,
}: {
  value: TemplateContentType;
  onChange: (v: TemplateContentType) => void;
  compact?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const current =
    TYPE_OPTIONS.find((t) => t.value === value) ?? TYPE_OPTIONS[0];

  return (
    <>
      <PillTrigger
        kicker="Type"
        leadIcon="fa-regular fa-tag"
        valueIcon={current.icon}
        value={current.label}
        open={open}
        onClick={(e) => setAnchor(e.currentTarget)}
        compact={compact}
      />
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: styles.menuPaper } }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <MenuHeader>Template type</MenuHeader>
        {TYPE_OPTIONS.map((opt) => {
          const selected = opt.value === value;
          return (
            <MenuItem
              key={opt.value}
              selected={selected}
              onClick={() => {
                setAnchor(null);
                if (!selected) onChange(opt.value);
              }}
              sx={styles.optionRich}
            >
              <Box sx={[styles.optionIcon, selected && styles.optionIconSel]}>
                <Box component="i" className={opt.icon} sx={{ fontSize: 14 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={styles.optionLabel}>{opt.label}</Typography>
                <Typography sx={styles.optionDesc}>
                  {opt.description}
                </Typography>
              </Box>
              {selected && (
                <Box
                  component="i"
                  className="fa-solid fa-check"
                  sx={{ fontSize: 12, color: "primary.main" }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export function FolderPill({
  folderId,
  folders,
  onChange,
  onNewFolder,
  compact = false,
}: {
  folderId: number | null;
  folders: TemplateFolder[];
  onChange: (id: number | null) => void;
  onNewFolder: () => void;
  compact?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const current = folders.find((f) => f.id === folderId);
  const displayName = current ? current.name : "All Templates";

  const close = () => setAnchor(null);

  return (
    <>
      <PillTrigger
        kicker="Folder"
        leadIcon="fa-regular fa-folder"
        value={displayName}
        open={open}
        onClick={(e) => setAnchor(e.currentTarget)}
        compact={compact}
      />
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: styles.menuPaper } }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <MenuHeader>Move to folder</MenuHeader>
        <Box sx={{ maxHeight: 264, overflowY: "auto" }}>
          <FolderOption
            key="root"
            label="All Templates"
            selected={folderId === null}
            onClick={() => {
              close();
              if (folderId !== null) onChange(null);
            }}
          />
          {folders.map((folder) => {
            const selected = folder.id === folderId;
            return (
              <FolderOption
                key={folder.id}
                label={folder.name}
                selected={selected}
                onClick={() => {
                  close();
                  if (!selected) onChange(folder.id);
                }}
              />
            );
          })}
        </Box>
        <Divider sx={{ my: 0.5, borderColor: "purple.light" }} />
        <MenuItem
          onClick={() => {
            close();
            onNewFolder();
          }}
          sx={styles.optionNew}
        >
          <Box
            component="i"
            className="fa-regular fa-folder-plus"
            sx={{ fontSize: 14, mr: 1 }}
          />
          New folder
        </MenuItem>
      </Menu>
    </>
  );
}

function FolderOption({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <MenuItem onClick={onClick} selected={selected} sx={styles.option}>
      <Box sx={[styles.optionIcon, selected && styles.optionIconSel]}>
        <Box
          component="i"
          className={
            selected ? "fa-regular fa-folder-open" : "fa-regular fa-folder"
          }
          sx={{ fontSize: 14 }}
        />
      </Box>
      <Typography sx={[styles.optionLabel, { flex: 1, minWidth: 0 }]} noWrap>
        {label}
      </Typography>
      {selected && (
        <Box
          component="i"
          className="fa-solid fa-check"
          sx={{ fontSize: 12, color: "primary.main" }}
        />
      )}
    </MenuItem>
  );
}

function MenuHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "text.secondary",
        px: 1.5,
        pt: 1,
        pb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function PillTrigger({
  kicker,
  leadIcon,
  valueIcon,
  value,
  open,
  onClick,
  compact = false,
}: {
  kicker: string;
  leadIcon?: string;
  valueIcon?: string;
  value: string;
  open: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  compact?: boolean;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={[
        styles.pillTrigger,
        compact && styles.pillTriggerCompact,
        open && styles.pillTriggerOpen,
      ]}
      aria-haspopup="listbox"
      aria-expanded={open}
    >
      {leadIcon && (
        <Box
          component="i"
          className={leadIcon}
          sx={{
            fontSize: compact ? 11 : 13,
            color: "text.secondary",
          }}
        />
      )}
      {!compact && (
        <Typography component="span" sx={styles.kicker}>
          {kicker}
        </Typography>
      )}
      <Box sx={[styles.valueWrap, compact && styles.valueWrapCompact]}>
        {valueIcon && (
          <Box
            component="i"
            className={valueIcon}
            sx={{
              fontSize: compact ? 10 : 12,
              color: "primary.main",
            }}
          />
        )}
        <Typography
          component="span"
          sx={[styles.value, compact && styles.valueCompact]}
        >
          {value}
        </Typography>
      </Box>
      <Box
        component="i"
        className="fa-solid fa-chevron-down"
        sx={{
          fontSize: compact ? 8 : 9,
          color: "text.secondary",
          ml: 0.25,
          transition: "transform 0.16s",
          transform: open ? "rotate(180deg)" : "none",
        }}
      />
    </ButtonBase>
  );
}

export function NichesPill({
  niches,
  selectedIds,
  onChange,
  compact = false,
}: {
  niches: OrganizationNiche[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  compact?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const selectedNiches = niches.filter((n) =>
    selectedIds.includes(n.id as number),
  );
  const displayValue =
    selectedNiches.length === 0
      ? "Select niches"
      : selectedNiches.length === 1
        ? selectedNiches[0].name ?? "1 selected"
        : `${selectedNiches.length} selected`;

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <>
      <PillTrigger
        kicker="Niches"
        leadIcon="fa-regular fa-cards-blank"
        value={displayValue}
        open={open}
        onClick={(e) => setAnchor(e.currentTarget)}
        compact={compact}
      />
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: styles.menuPaper } }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <MenuHeader>Assign to niches</MenuHeader>
        <Box sx={{ maxHeight: 264, overflowY: "auto" }}>
          {niches.map((niche) => {
            const selected = selectedIds.includes(niche.id as number);
            return (
              <MenuItem
                key={niche.id}
                selected={selected}
                onClick={() => toggle(niche.id as number)}
                sx={styles.option}
              >
                <Box sx={[styles.optionIcon, selected && styles.optionIconSel]}>
                  <Box
                    component="i"
                    className="fa-regular fa-tag"
                    sx={{ fontSize: 14 }}
                  />
                </Box>
                <Typography
                  sx={[styles.optionLabel, { flex: 1, minWidth: 0 }]}
                  noWrap
                >
                  {niche.name}
                </Typography>
                {selected && (
                  <Box
                    component="i"
                    className="fa-solid fa-check"
                    sx={{ fontSize: 12, color: "primary.main" }}
                  />
                )}
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </>
  );
}

export function ActionPill({
  icon,
  label,
  onClick,
  compact = false,
}: {
  icon: string;
  label: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  compact?: boolean;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={[styles.pillTrigger, compact && styles.pillTriggerCompact]}
    >
      <Box
        component="i"
        className={icon}
        sx={{
          fontSize: compact ? 11 : 13,
          color: "primary.main",
        }}
      />
      <Typography
        component="span"
        sx={[styles.value, compact && styles.valueCompact]}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

const styles = {
  pillTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.875,
    bgcolor: "#fff",
    border: "1px solid",
    borderColor: "purple.light",
    borderRadius: 999,
    px: 1.25,
    py: 0.75,
    color: "text.primary",
    whiteSpace: "nowrap",
    transition: "background-color 0.12s, border-color 0.12s, box-shadow 0.12s",
    "&:hover": {
      bgcolor: "grey.50",
      borderColor: "grey.300",
    },
  },
  pillTriggerOpen: {
    borderColor: "primary.main",
    boxShadow: "0 0 0 3px rgba(1, 70, 56, 0.10)",
  },
  pillTriggerCompact: {
    gap: 0.5,
    px: 0.875,
    py: 0.375,
  },
  kicker: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "text.disabled",
  },
  valueWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.625,
    fontSize: 13,
    fontWeight: 600,
  },
  valueWrapCompact: {
    gap: 0.375,
    fontSize: 11.5,
  },
  value: {
    fontSize: 13,
    fontWeight: 600,
    color: "text.primary",
  },
  valueCompact: {
    fontSize: 11.5,
  },
  menuPaper: {
    borderRadius: 2,
    border: "1px solid",
    borderColor: "purple.light",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06), 0 12px 32px rgba(20, 30, 22, 0.18)",
    minWidth: 244,
    p: 0.75,
  },
  option: {
    borderRadius: 1.25,
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    px: 1.25,
    py: 1,
    "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "primary.light" },
  },
  optionRich: {
    borderRadius: 1.25,
    display: "flex",
    alignItems: "flex-start",
    gap: 1.25,
    px: 1.25,
    py: 1,
    "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "primary.light" },
  },
  optionIcon: {
    width: 30,
    height: 30,
    borderRadius: 1.125,
    bgcolor: "grey.100",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "primary.main",
    flexShrink: 0,
  },
  optionIconSel: {
    bgcolor: "primary.main",
    color: "#fff",
  },
  optionLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "text.primary",
    lineHeight: 1.2,
  },
  optionDesc: {
    fontSize: 11.5,
    color: "text.secondary",
    lineHeight: 1.4,
    mt: 0.25,
  },
  optionNew: {
    borderRadius: 1.25,
    fontSize: 12.5,
    fontWeight: 500,
    color: "text.secondary",
    px: 1.25,
    py: 1,
  },
};
